import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
import { CampaignList } from '../components/CampaignList';
import { Campaign } from '../../../shared/types/Campaign';
// Mock campaign data based on screenshots
const mockCampaigns: Campaign[] = [{
  id: 'BILASHOP2025',
  title: '[BILA Mart] 7-days Promotion for Shop',
  description: '[BILA Mart] 7-days Promotion for Shop',
  expiryDate: '07/03/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 50,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 5000,
  budgetUsed: 0,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILASHOP2025',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 50,
  maxDiscount: 50,
  status: 'active',
  companies: ['Bank Rakyat Life'],
  transactionVolume: 47250,
  transactionCount: 63
}, {
  id: 'BILA10MYR',
  title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
  description: 'DISCOUNT_COUPON',
  expiryDate: '02/01/2026',
  discountPercentage: 0,
  fixedDiscountAmount: 10,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budget: 11000,
  budgetUsed: 10960,
  strictlyEnforceBudget: true,
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: 'BILA10MYR',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 10,
  maxDiscount: 10,
  status: 'active',
  transactionVolume: 1284500,
  transactionCount: 1096
}, {
  id: 'BULK2025',
  title: 'Summer Festival Bulk Codes',
  description: 'Unique codes for summer festival attendees',
  startDate: '01/11/2025',
  expiryDate: '31/01/2026',
  discountPercentage: 15,
  fixedDiscountAmount: 0,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 50000,
  budgetUsed: 12500,
  strictlyEnforceBudget: true,
  paymentMethods: ['Card', 'PromptPay'],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Bulk',
  minTransactionValue: 500,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 1000,
  status: 'active',
  transactionVolume: 312750,
  transactionCount: 245
}];
export function Campaigns() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const handleCreateCampaign = () => {
    navigate('/campaign-form');
  };
  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign-details?id=${campaignId}`);
  };
  const filteredCampaigns = mockCampaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.id.toLowerCase().includes(searchTerm.toLowerCase()) || campaign.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Calculate summary metrics
  const activeCampaigns = mockCampaigns.filter(c => c.status === 'active');
  const totalBudget = activeCampaigns.reduce((sum, c) => sum + c.budget, 0);
  const totalUsed = activeCampaigns.reduce((sum, c) => sum + c.budgetUsed, 0);
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-11863">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11864">
        <div data-id="element-11865">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11866">
            {t('pages.Campaigns.campaigns')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-11867">
            {t('pages.Campaigns.manage_your_discount_codes_and_promotional')}
          </p>
        </div>
        <button onClick={handleCreateCampaign} className="px-4 py-2 bg-[#0066b3] hover:bg-[#005291] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11868">
          {t('pages.Campaigns.create_campaign')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11869">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11870">
          <div className="flex items-center gap-3 mb-2" data-id="element-11871">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-11872">
              <TagIcon size={20} data-id="element-11873" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11874">
              {t('pages.Campaigns.active_campaigns')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11875">
            {activeCampaigns.length}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11876">
          <div className="flex items-center gap-3 mb-2" data-id="element-11877">
            <div className="p-2 bg-green-50 text-green-600 rounded-lg" data-id="element-11878">
              <PercentIcon size={20} data-id="element-11879" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11880">
              {t('pages.Campaigns.total_redemptions')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11881">1,248</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11882">
          <div className="flex items-center gap-3 mb-2" data-id="element-11883">
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg" data-id="element-11884">
              <DollarSignIcon size={20} data-id="element-11885" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11886">
              {t('pages.Campaigns.budget_allocated')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11887">
            {t('components.ShopDashboard.usd_2')} {totalBudget.toLocaleString()}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-11888">
          <div className="flex items-center gap-3 mb-2" data-id="element-11889">
            <div className="p-2 bg-[#E6F1F9] text-[#004f87] rounded-lg" data-id="element-11890">
              <DollarSignIcon size={20} data-id="element-11891" />
            </div>
            <div className="text-sm font-medium text-gray-500" data-id="element-11892">
              {t('pages.Campaigns.budget_used')}
            </div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-11893">
            {t('components.ShopDashboard.usd_2')} {totalUsed.toLocaleString()}
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden" data-id="element-11894">
            <div className="bg-[#0066b3] h-1.5 rounded-full" style={{
            width: `${totalUsed / totalBudget * 100}%`
          }} data-id="element-11895"></div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center" data-id="element-11896">
        <div className="relative w-full sm:w-96" data-id="element-11897">
          <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11898" />

          <input type="text" placeholder={t('pages.Campaigns.attrs.search_campaigns_by_name_id_or')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] focus:outline-none transition-colors" data-id="element-11899" />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-11900">
          <span className="text-sm text-gray-500 font-medium" data-id="element-11901">
            {t('pages.Campaigns.status')}
          </span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-[#0066b3]/20 focus:border-[#0066b3] focus:outline-none transition-colors" data-id="element-11902">
            <option value="all" data-id="element-11903">{t('pages.Campaigns.all_statuses')}</option>
            <option value="active" data-id="element-11904">{t('pages.Campaigns.active')}</option>
            <option value="inactive" data-id="element-11905">{t('pages.Campaigns.inactive')}</option>
            <option value="expired" data-id="element-11906">{t('pages.Campaigns.expired')}</option>
          </select>
        </div>
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11907">
        <CampaignList campaigns={filteredCampaigns} onCampaignClick={handleCampaignClick} data-id="element-11908" />
      </div>
    </div>;
}